import { Component, DestroyRef, effect, inject, input, model, OnDestroy, OnInit } from '@angular/core';

import { ButtonModule, IconModule } from '@hglobal/fin-ui';
import { FormGroup } from '@ngneat/reactive-forms';
import { TranslateModule } from '@ngx-translate/core';

import { CopyTooltipComponent, WalletCardComponent } from '@kr/components';
import { ExchangeStore } from '@kr/components/exchange/exchange.store';
import { ExchangeEstimateReq } from '@kr/components/exchange/models';
import { ExchangeFormSepaSwiftModel } from '@kr/components/exchange/models/exchange-form-sepa-swift.model';
import { isUsdtSymbol, WalletCard } from '@kr/models';
import { CurrencyBalanceFormatPipe } from '@kr/pipes';

@Component({
  selector: 'kr-crypto-fiat-details',
  imports: [ButtonModule, CopyTooltipComponent, CurrencyBalanceFormatPipe, IconModule, TranslateModule, WalletCardComponent],
  templateUrl: './crypto-fiat-details.component.html',
  styleUrl: './crypto-fiat-details.component.scss',
})
export class CryptoFiatDetailsComponent implements OnInit, OnDestroy {
  #duration = 60;
  #step = 1000;
  #intervalId: any = null;
  #endTime: number;

  readonly destroyRef = inject(DestroyRef);
  readonly store = inject(ExchangeStore);

  form = input<FormGroup<ExchangeFormSepaSwiftModel>>();
  wallets = input<WalletCard[]>();
  estimation = this.store.exchangeEstimate;

  walletFrom: WalletCard;

  timeLeft = model(this.#duration);

  constructor() {
    effect(() => {
      if (this.estimation()) {
        this.startTimer();
      }
    });
  }

  isUsdt(symbol: string): string {
    return String(isUsdtSymbol(symbol));
  }

  ngOnInit(): void {
    this.walletFrom = this.wallets().find(wallet => wallet.id == this.form().value['walletFromId'].toString());

    this.store.saveWalletOrgId(this.walletFrom.organizationId);

    this.getEstimation();
  }

  startTimer() {
    if (this.#intervalId) {
      return;
    }

    const now = Date.now();
    this.#endTime = now + this.#duration * 1000;

    this.updateTimeLeft();
    this.#intervalId = setInterval(() => {
      this.updateTimeLeft();
    }, this.#step);
  }

  updateTimeLeft() {
    const now = Date.now();
    const secondsLeft = Math.max(0, Math.floor((this.#endTime - now) / 1000));
    this.timeLeft.set(secondsLeft);

    if (secondsLeft === 0) {
      this.stopTimer();
    }
  }

  resetTimer() {
    this.stopTimer();
    this.store.clearEstimate();
    this.getEstimation();
  }

  stopTimer() {
    if (this.#intervalId !== null) {
      clearInterval(this.#intervalId);
      this.#intervalId = null;
    }
  }

  getEstimation(): void {
    const request: ExchangeEstimateReq = {
      walletFromId: this.form().value.walletFromId,
      amount: String(this.form().value.amount ?? this.form().value.amountReceive),
      toReceive: !!this.form().value.amountReceive,
    };

    if (this.form().value.transferType === 'sepa') {
      request.bankAccountSEPA = {
        beneficiaryAddress: this.form().value.beneficiaryAddress,
        beneficiaryName: this.form().value.beneficiaryName,
        bic: this.form().value.BICOrSWIFTCode,
        iban: this.form().value.iban,
        incorporateNum: this.form().value.incorporateNum,
        reference: this.form().value.reference,
      };
    }

    if (this.form().value.transferType === 'swift') {
      request.bankAccountSWIFT = {
        BICOrSWIFTCode: this.form().value.BICOrSWIFTCode,
        beneficiaryAddress: this.form().value.beneficiaryAddress,
        beneficiaryBankAddress: this.form().value.beneficiaryBankAddress,
        beneficiaryBankName: this.form().value.beneficiaryBankName,
        beneficiaryName: this.form().value.beneficiaryName,
        correspondentBankAddress: this.form().value.correspondentBankAddress,
        correspondentBankBICOrSWIFTCode: this.form().value.correspondentBankBICOrSWIFTCode,
        correspondentBankName: this.form().value.correspondentBankName,
        currency: this.form().value.currency,
        iban: this.form().value.iban,
        incorporateNum: this.form().value.incorporateNum,
        reference: this.form().value.reference,
      };
    }

    this.store.getExchangeEstimate(request);
  }

  ngOnDestroy(): void {
    this.stopTimer();
  }
}
